import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { searchProducts, getAllCategories, getAllStores, getWishlist, getOffers } from '@/api';

interface CategoryOption {
  id: number;
  name: string;
}

interface BrandOption {
  id: number;
  name: string;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  preselectedCategory?: number | null;
  onClearCategory?: () => void;
  initialShowFilters?: boolean;
  inline?: boolean;
  externalQuery?: string;
  filterOpenTrigger?: number;
}

interface SearchResult {
  id: string;
  type: 'product' | 'category' | 'wishlist' | 'offer' | 'address' | 'vendor' | 'story';
  title: string;
  subtitle?: string;
  image?: string;
  price?: string;
  path: string;
  icon: string;
  storeAddress?: string;
  storeTag?: string;
  storeRating?: number;
  storeReviewsCount?: number;
  storeProductsCount?: number;
}

interface FilterState {
  sortBy: string;
  priceRange: string;
  category: string;
  rating: string;
  brand: string;
  color: string;
  size: string;
}

export default function SearchModal({ isOpen, onClose, isDarkMode, preselectedCategory, onClearCategory, initialShowFilters, inline, externalQuery, filterOpenTrigger }: SearchModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [brands, setBrands] = useState<BrandOption[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [colors, setColors] = useState<string[]>([]);
  const [colorsLoading, setColorsLoading] = useState(false);
  const [sizes, setSizes] = useState<string[]>([]);
  const [sizesLoading, setSizesLoading] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    sortBy: 'relevance',
    priceRange: 'all',
    category: preselectedCategory ? String(preselectedCategory) : 'all',
    rating: 'all',
    brand: 'all',
    color: 'all',
    size: 'all',
  });
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [searchMode, setSearchMode] = useState<'delivery' | 'pickup' | 'scheduled'>(() => {
    const pm = localStorage.getItem('pickupMode');
    if (pm === 'self') return 'pickup';
    if (pm === 'scheduled') return 'scheduled';
    return 'delivery';
  });
  const [resultTab, setResultTab] = useState<'all' | 'products' | 'stores' | 'categories'>('all');
  const [storeResults, setStoreResults] = useState<SearchResult[]>([]);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isOpen]);

  // Check reduced motion preference
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Sync searchMode with localStorage pickupMode changes
  useEffect(() => {
    const handleStorage = () => {
      const pm = localStorage.getItem('pickupMode');
      if (pm === 'self') setSearchMode('pickup');
      else if (pm === 'scheduled') setSearchMode('scheduled');
      else setSearchMode('delivery');
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Load categories, brands, colors, sizes for filter
  useEffect(() => {
    if (!isOpen) return;

    const loadCategories = async () => {
      setCategoriesLoading(true);
      try {
        const data = await getAllCategories();
        if (Array.isArray(data) && data.length > 0) {
          setCategories(data.map((c: any) => ({ id: c.id, name: c.name })));
        }
      } catch {
        // ignore
      } finally {
        setCategoriesLoading(false);
      }
    };

    const loadBrands = async () => {
      setBrandsLoading(true);
      try {
        const data = await getAllStores();
        const list = Array.isArray(data) ? data : (data as any)?.stores || [];
        if (list.length > 0) {
          setBrands(list.map((s: any) => ({ id: s.id, name: s.name })));
        }
      } catch {
        // ignore
      } finally {
        setBrandsLoading(false);
      }
    };

    // Color/size filters aren't backed by the REST API yet — leave empty gracefully.
    const loadColors = async () => {
      setColorsLoading(true);
      setColors([]);
      setColorsLoading(false);
    };

    const loadSizes = async () => {
      setSizesLoading(true);
      setSizes([]);
      setSizesLoading(false);
    };

    loadCategories();
    loadBrands();
    loadColors();
    loadSizes();
  }, [isOpen]);

  // Update filter when preselectedCategory changes
  useEffect(() => {
    if (preselectedCategory) {
      setFilters(prev => ({ ...prev, category: String(preselectedCategory) }));
    } else {
      setFilters(prev => ({ ...prev, category: 'all' }));
    }
  }, [preselectedCategory]);

  // Show filters panel when initialShowFilters is true
  useEffect(() => {
    if (initialShowFilters && isOpen) {
      setShowFilterModal(true);
    }
  }, [initialShowFilters, isOpen]);

  // Open filter modal when triggered from parent (inline mode)
  useEffect(() => {
    if (inline && filterOpenTrigger && filterOpenTrigger > 0) {
      setShowFilterModal(true);
    }
  }, [inline, filterOpenTrigger]);

  // Sync externalQuery when in inline mode
  useEffect(() => {
    if (inline && externalQuery !== undefined) {
      setQuery(externalQuery);
    }
  }, [inline, externalQuery]);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setStoreResults([]);
      setResultTab('all');
      setShowFilterModal(false);
      if (onClearCategory) onClearCategory();
    }
  }, [isOpen]);

  // Load recent searches
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      try { setRecentSearches(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    let count = 0;
    if (filters.sortBy !== 'relevance') count++;
    if (filters.priceRange !== 'all') count++;
    if (filters.category !== 'all') count++;
    if (filters.rating !== 'all') count++;
    if (filters.brand !== 'all') count++;
    if (filters.color !== 'all') count++;
    if (filters.size !== 'all') count++;
    setActiveFiltersCount(count);
  }, [filters]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Search everything
  const performSearch = useCallback(async (q: string, currentFilters: FilterState) => {
    if (!q.trim()) {
      setResults([]);
      setStoreResults([]);
      return;
    }
    setLoading(true);
    const allResults: SearchResult[] = [];
    const storeRes: SearchResult[] = [];

    try {
      // 1. Search products with category filter
      const apiProducts = await searchProducts(q, 20).catch(() => ({ products: [] }));
      const combinedProds = ((apiProducts as any)?.products || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        price: p.discount_enabled && p.discount_price ? p.discount_price : (p.discountEnabled && p.discountPrice ? p.discountPrice : p.price),
        originalPrice: (p.discount_enabled && p.discount_price) || (p.discountEnabled && p.discountPrice) ? p.price : undefined,
        image: p.images?.[0] || p.media?.[0]?.url,
        categoryName: p.categoryName ?? p.category_name,
        categoryId: p.categoryId ?? p.category_id,
        storeId: p.storeId ?? p.store_id,
        storeName: p.storeName ?? p.store_name,
      }));

      // Apply category filter
      let filteredProds = combinedProds;
      if (currentFilters.category !== 'all') {
        const catId = Number(currentFilters.category);
        filteredProds = combinedProds.filter((p: any) => {
          return String(p.categoryId) === String(catId) ||
                 p.categoryName === categories.find(c => c.id === catId)?.name;
        });
      }

      // Apply brand filter
      if (currentFilters.brand !== 'all') {
        const brandId = Number(currentFilters.brand);
        filteredProds = filteredProds.filter((p: any) => {
          return String(p.storeId) === String(brandId) ||
                 p.storeName === brands.find(b => b.id === brandId)?.name;
        });
      }

      // Color/size filters aren't backed by the REST API yet — no-op (colors/sizes lists are empty).

      // Apply price filter
      if (currentFilters.priceRange !== 'all') {
        const [min, max] = currentFilters.priceRange.split('-').map(Number);
        filteredProds = filteredProds.filter((p: any) => {
          const price = p.price || p.finalPrice || 0;
          if (currentFilters.priceRange === '500+') return price >= 500;
          return price >= min && price <= max;
        });
      }

      filteredProds.slice(0, 8).forEach((p: any) => {
        const price = p.price || p.finalPrice || p.originalPrice || 0;
        const origPrice = p.originalPrice || p.price;
        const hasDiscount = origPrice && price < origPrice;
        allResults.push({
          id: `product-${p.id}`,
          type: 'product',
          title: p.name,
          subtitle: hasDiscount
            ? `${t('product.price')}: ${price.toLocaleString()} ${t('common.currency')}`
            : p.categoryName || p.storeName || t('product.price'),
          image: p.image || p.media?.[0]?.url || p.thumbnail,
          price: price?.toLocaleString(),
          path: `/product/${p.id}`,
          icon: 'ri-shopping-bag-3-line',
        });
      });

      // 2. Search stores / vendors - search by name
      const storesRes = await getAllStores(1, 50).catch(() => []);
      const storeList = Array.isArray(storesRes) ? storesRes : (storesRes as any)?.stores || [];
      const matchingStores = storeList.filter((s: any) =>
        s.name?.toLowerCase().includes(q.toLowerCase()) ||
        s.address?.toLowerCase().includes(q.toLowerCase())
      ).slice(0, 6);
      matchingStores.forEach((s: any) => {
        storeRes.push({
          id: `store-${s.id}`,
          type: 'vendor',
          title: s.name,
          subtitle: s.address || '',
          image: s.logo || s.banner,
          path: `/store/${s.id}`,
          icon: 'ri-store-2-line',
          storeAddress: s.address,
          storeTag: s.tag,
          storeRating: s.rating ? Number(s.rating) : undefined,
          storeReviewsCount: s.reviewsCount ?? s.reviews_count,
          storeProductsCount: s.productsCount ?? s.products_count,
        });
      });

      // 3. Search categories (only when no category filter)
      if (currentFilters.category === 'all') {
        const matchingCats = categories.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())).slice(0, 5);
        matchingCats.forEach((c) => {
          const productCount = combinedProds.filter((p: any) => String(p.categoryId) === String(c.id) || p.categoryName === c.name).length;
          allResults.push({
            id: `cat-${c.id}`,
            type: 'category',
            title: c.name,
            subtitle: `${productCount} ${t('categories.productsAvailable') || 'محصول'}`,
            path: `/categories?category=${c.id}`,
            icon: 'ri-apps-2-line',
          });
        });
      }

      // 4. Search wishlist
      const wishRes = await getWishlist().catch(() => []);
      const wishItems = (Array.isArray(wishRes) ? wishRes : []).filter((w: any) =>
        w.name?.toLowerCase().includes(q.toLowerCase())
      ).slice(0, 4);
      wishItems.forEach((w: any) => {
        allResults.push({
          id: `wish-${w.id}`,
          type: 'wishlist',
          title: w.name,
          subtitle: t('wishlist.saved') || 'Saved',
          image: w.image || w.thumbnail,
          path: `/wishlist`,
          icon: 'ri-heart-3-line',
        });
      });

      // 5. Search offers
      const offersRes = await getOffers(10).catch(() => []);
      const offers = (Array.isArray(offersRes) ? offersRes : []).filter((o: any) =>
        o.name?.toLowerCase().includes(q.toLowerCase()) ||
        o.title?.toLowerCase().includes(q.toLowerCase())
      ).slice(0, 4);
      offers.forEach((o: any) => {
        allResults.push({
          id: `offer-${o.id}`,
          type: 'offer',
          title: o.title || o.name,
          subtitle: o.discountText || `${o.discountPercent || 0}% ${t('product.discount') || 'OFF'}`,
          image: o.image || o.banner,
          path: `/product/${o.productId || o.id}`,
          icon: 'ri-percent-line',
        });
      });

      setResults(allResults);
      setStoreResults(storeRes);
    } catch {
      setResults([]);
      setStoreResults([]);
    } finally {
      setLoading(false);
    }
  }, [t, categories, brands]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim()) {
        performSearch(query, filters);
      } else {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, performSearch, filters]);

  const saveRecent = (q: string) => {
    const updated = [q, ...recentSearches.filter(s => s !== q)].slice(0, 8);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  const deleteRecent = (q: string) => {
    const updated = recentSearches.filter(s => s !== q);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  const handleResultClick = (result: SearchResult) => {
    saveRecent(query || result.title);
    onClose();
    navigate(result.path);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      saveRecent(query);
      performSearch(query, filters);
    }
  };

  const groupedResults = results.reduce((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  const typeOrder = ['product', 'category', 'wishlist', 'offer', 'address', 'vendor'];

  const selectedCategoryName = categories.find(c => c.id === Number(filters.category))?.name || '';
  const selectedBrandName = brands.find(b => b.id === Number(filters.brand))?.name || '';

  const sortOptions = [
    { key: 'relevance', label: t('search.relevance') || 'Relevance' },
    { key: 'price-asc', label: t('search.priceLowToHigh') || 'Price: Low to High' },
    { key: 'price-desc', label: t('search.priceHighToLow') || 'Price: High to Low' },
    { key: 'newest', label: t('search.newest') || 'Newest' },
    { key: 'rating', label: t('search.topRated') || 'Top Rated' },
  ];

  const priceRanges = [
    { key: 'all', label: t('search.allPrices') || 'All Prices' },
    { key: '0-50', label: '$0 - $50' },
    { key: '50-100', label: '$50 - $100' },
    { key: '100-200', label: '$100 - $200' },
    { key: '200-500', label: '$200 - $500' },
    { key: '500+', label: '$500+' },
  ];

  const ratingOptions = [
    { key: 'all', label: t('search.allRatings') || 'All Ratings' },
    { key: '4', label: '4+ Stars' },
    { key: '4.5', label: '4.5+ Stars' },
    { key: '5', label: '5 Stars' },
  ];

  // ==================== INLINE MODE RENDER ====================
  if (inline) {
    return (
      <>
        <div className={`${isDarkMode ? 'bg-dark-100/50 border-dark-200/30' : 'bg-white/50 border-stone-200/30'} border-t backdrop-blur-xl min-h-full`}>
          {/* Content Area */}
          <div className="px-4 pb-4">

            {/* Recent Searches — shown when no query */}
            {!query && recentSearches.length > 0 && (
              <div className="py-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('home.recentSearches') || 'Recent Searches'}
                  </h4>
                  <button
                    onClick={() => { setRecentSearches([]); localStorage.removeItem('recentSearches'); }}
                    className="text-xs text-gold hover:text-gold-dark cursor-pointer"
                  >
                    {t('common.clear') || 'Clear All'}
                  </button>
                </div>
                <div className="space-y-2">
                  {recentSearches.map((rs, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all cursor-pointer group ${
                        isDarkMode
                          ? 'bg-dark-200/15 hover:bg-dark-200/35 border border-dark-200/10'
                          : 'bg-white/25 hover:bg-white/45 border border-stone-200/10'
                      }`}
                    >
                      <button
                        onClick={() => setQuery(rs)}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                      >
                        <i className="ri-time-line text-gold text-sm flex-shrink-0" />
                        <span className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{rs}</span>
                      </button>
                      <button
                        onClick={() => deleteRecent(rs)}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer ${
                          isDarkMode ? 'hover:bg-red-500/10 text-gray-500 hover:text-red-400' : 'hover:bg-red-50 text-gray-400 hover:text-red-500'
                        }`}
                      >
                        <i className="ri-close-line text-sm" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!query && recentSearches.length === 0 && !loading && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="py-10 flex flex-col items-center gap-3"
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-dark-200/30 border border-dark-200/20' : 'bg-white/50 border border-stone-200/20'} backdrop-blur-md`}>
                  <i className={`ri-search-line text-2xl ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                </div>
                <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t('search.startSearching') || 'Start searching'}
                </p>
              </motion.div>
            )}

            {/* Loading */}
            {loading && (
              <div className="py-8 flex flex-col items-center gap-3">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                  className="w-8 h-8 rounded-full border-2 border-gold/20 border-t-gold"
                />
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t('common.loading') || 'Searching...'}
                </span>
              </div>
            )}

            {/* No results */}
            {!loading && query && results.length === 0 && storeResults.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="py-10 flex flex-col items-center gap-3"
              >
                <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t('errors.noResults') || 'No results found'}
                </p>
              </motion.div>
            )}

            {/* Results */}
            {!loading && query && (results.length > 0 || storeResults.length > 0) && (
              <div className="py-3 space-y-3">

                {/* Stores */}
                {storeResults.length > 0 && (
                  <div className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-dark-200/30 border border-dark-200/20' : 'bg-white/40 border border-stone-200/20'}`}>
                    <div className={`flex items-center gap-2 px-4 py-2.5 border-b ${isDarkMode ? 'border-dark-200/20 bg-white/[0.03]' : 'border-stone-200/20 bg-black/[0.02]'}`}>
                      <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        {t('search.stores') || 'Stores'}
                      </span>
                      <span className={`text-[10px] opacity-50 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {storeResults.length}
                      </span>
                    </div>
                    <div>
                      {storeResults.map((result, idx) => (
                        <button
                          key={result.id}
                          onClick={() => handleResultClick(result)}
                          className={`w-full px-4 py-3 text-left transition-colors cursor-pointer ${
                            isDarkMode ? 'text-gray-200 hover:bg-dark-200/20' : 'text-gray-800 hover:bg-white/50'
                          } ${idx !== storeResults.length - 1 ? (isDarkMode ? 'border-b border-dark-200/15' : 'border-b border-stone-200/15') : ''}`}
                        >
                          <p className="text-sm font-medium">{result.title}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Products */}
                {groupedResults['product'] && groupedResults['product'].length > 0 && (
                  <div className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-dark-200/30 border border-dark-200/20' : 'bg-white/40 border border-stone-200/20'}`}>
                    <div className={`flex items-center gap-2 px-4 py-2.5 border-b ${isDarkMode ? 'border-dark-200/20 bg-white/[0.03]' : 'border-stone-200/20 bg-black/[0.02]'}`}>
                      <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        {t('search.items') || 'Items'}
                      </span>
                      <span className={`text-[10px] opacity-50 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {groupedResults['product'].length}
                      </span>
                    </div>
                    <div>
                      {groupedResults['product'].slice(0, 6).map((result, idx) => (
                        <button
                          key={result.id}
                          onClick={() => handleResultClick(result)}
                          className={`w-full px-4 py-3 text-left transition-colors cursor-pointer ${
                            isDarkMode ? 'text-gray-200 hover:bg-dark-200/20' : 'text-gray-800 hover:bg-white/50'
                          } ${idx !== groupedResults['product'].slice(0, 6).length - 1 ? (isDarkMode ? 'border-b border-dark-200/15' : 'border-b border-stone-200/15') : ''}`}
                        >
                          <p className="text-sm font-medium">{result.title}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Categories */}
                {groupedResults['category'] && groupedResults['category'].length > 0 && (
                  <div className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-dark-200/30 border border-dark-200/20' : 'bg-white/40 border border-stone-200/20'}`}>
                    <div className={`flex items-center gap-2 px-4 py-2.5 border-b ${isDarkMode ? 'border-dark-200/20 bg-white/[0.03]' : 'border-stone-200/20 bg-black/[0.02]'}`}>
                      <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        {t('search.categories') || 'Categories'}
                      </span>
                      <span className={`text-[10px] opacity-50 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {groupedResults['category'].length}
                      </span>
                    </div>
                    <div>
                      {groupedResults['category'].map((result, idx) => (
                        <button
                          key={result.id}
                          onClick={() => handleResultClick(result)}
                          className={`w-full px-4 py-3 text-left transition-colors cursor-pointer ${
                            isDarkMode ? 'text-gray-200 hover:bg-dark-200/20' : 'text-gray-800 hover:bg-white/50'
                          } ${idx !== groupedResults['category'].length - 1 ? (isDarkMode ? 'border-b border-dark-200/15' : 'border-b border-stone-200/15') : ''}`}
                        >
                          <p className="text-sm font-medium">{result.title}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* View All Results */}
                {query.trim() && (
                  <button
                    onClick={() => {
                      saveRecent(query);
                      navigate(`/search?q=${encodeURIComponent(query)}`);
                      onClose();
                    }}
                    className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-semibold transition-all cursor-pointer ${
                      isDarkMode
                        ? 'bg-gold/15 text-gold border border-gold/25 hover:bg-gold/25'
                        : 'bg-gold/10 text-gold-dark border border-gold/20 hover:bg-gold/20'
                    }`}
                  >
                    <i className="ri-arrow-right-line" />
                    <span>
                      {t('search.viewAllResults') || 'View all results for'} &lsquo;{query}&rsquo;
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Filter Modal */}
        <AnimatePresence>
          {showFilterModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[210] flex items-end md:items-center justify-center"
              onClick={() => setShowFilterModal(false)}
            >
              <div className={`absolute inset-0 ${isDarkMode ? 'bg-black/60' : 'bg-white/60'} backdrop-blur-sm`} />
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                className={`relative w-full md:max-w-md md:rounded-2xl rounded-t-3xl ${isDarkMode ? 'bg-dark-100/80 border-dark-200/30 backdrop-blur-xl' : 'bg-white/80 border-stone-200/30 backdrop-blur-xl'} border shadow-2xl max-h-[80vh] overflow-hidden flex flex-col`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex justify-center pt-3 pb-1">
                  <div className={`w-10 h-1 rounded-full ${isDarkMode ? 'bg-dark-300' : 'bg-gray-300'}`} />
                </div>
                <div className={`flex items-center justify-between px-5 pb-3 border-b ${isDarkMode ? 'border-dark-200/50' : 'border-gray-200/50'}`}>
                  <h3 className="text-lg font-bold">{t('search.filters') || 'Filters'}</h3>
                  <div className="flex items-center gap-2">
                    {activeFiltersCount > 0 && (
                      <button
                        onClick={() => setFilters({ sortBy: 'relevance', priceRange: 'all', category: 'all', rating: 'all', brand: 'all', color: 'all', size: 'all' })}
                        className="text-xs text-gold hover:text-gold-dark cursor-pointer"
                      >
                        {t('common.reset') || 'Reset'}
                      </button>
                    )}
                    <button
                      onClick={() => setShowFilterModal(false)}
                      className={`w-8 h-8 rounded-xl flex items-center justify-center cursor-pointer ${isDarkMode ? 'bg-dark-200/30 hover:bg-dark-300/40 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                    >
                      <i className="ri-close-line" />
                    </button>
                  </div>
                </div>
                {/* Filter Options */}
                <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
                  {/* Sort By */}
                  <div>
                    <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('search.sortBy') || 'Sort By'}</h4>
                    <div className="flex flex-wrap gap-2">
                      {sortOptions.map((opt) => (
                        <button key={opt.key} onClick={() => setFilters(prev => ({ ...prev, sortBy: opt.key }))}
                          className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filters.sortBy === opt.key ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25' : isDarkMode ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20' : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'}`}>{opt.label}</button>
                      ))}
                    </div>
                  </div>
                  {/* Category */}
                  <div>
                    <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('nav.categories') || 'Categories'}</h4>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => setFilters(prev => ({ ...prev, category: 'all' }))} className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filters.category === 'all' ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25' : isDarkMode ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20' : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'}`}>{t('categories.all') || 'All'}</button>
                      {categories.map((cat) => (
                        <button key={cat.id} onClick={() => setFilters(prev => ({ ...prev, category: String(cat.id) }))} className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filters.category === String(cat.id) ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25' : isDarkMode ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20' : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'}`}>{cat.name}</button>
                      ))}
                    </div>
                  </div>
                  {/* Brand */}
                  <div>
                    <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('search.brand') || 'Brand'}</h4>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => setFilters(prev => ({ ...prev, brand: 'all' }))} className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filters.brand === 'all' ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25' : isDarkMode ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20' : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'}`}>{t('search.allBrands') || 'All Brands'}</button>
                      {brands.map((brand) => (
                        <button key={brand.id} onClick={() => setFilters(prev => ({ ...prev, brand: String(brand.id) }))} className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filters.brand === String(brand.id) ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25' : isDarkMode ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20' : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'}`}>{brand.name}</button>
                      ))}
                    </div>
                  </div>
                  {/* Color */}
                  <div>
                    <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('search.color') || 'Color'}</h4>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => setFilters(prev => ({ ...prev, color: 'all' }))} className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filters.color === 'all' ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25' : isDarkMode ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20' : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'}`}>{t('search.allColors') || 'All Colors'}</button>
                      {colors.map((color) => (
                        <button key={color} onClick={() => setFilters(prev => ({ ...prev, color }))} className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filters.color === color ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25' : isDarkMode ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20' : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'}`}>{color}</button>
                      ))}
                    </div>
                  </div>
                  {/* Size */}
                  <div>
                    <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('search.size') || 'Size'}</h4>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => setFilters(prev => ({ ...prev, size: 'all' }))} className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filters.size === 'all' ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25' : isDarkMode ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20' : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'}`}>{t('search.allSizes') || 'All Sizes'}</button>
                      {sizes.map((size) => (
                        <button key={size} onClick={() => setFilters(prev => ({ ...prev, size }))} className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filters.size === size ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25' : isDarkMode ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20' : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'}`}>{size}</button>
                      ))}
                    </div>
                  </div>
                  {/* Price Range */}
                  <div>
                    <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('search.priceRange') || 'Price Range'}</h4>
                    <div className="flex flex-wrap gap-2">
                      {priceRanges.map((opt) => (
                        <button key={opt.key} onClick={() => setFilters(prev => ({ ...prev, priceRange: opt.key }))} className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filters.priceRange === opt.key ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25' : isDarkMode ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20' : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'}`}>{opt.label}</button>
                      ))}
                    </div>
                  </div>
                  {/* Rating */}
                  <div>
                    <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('search.rating') || 'Rating'}</h4>
                    <div className="flex flex-wrap gap-2">
                      {ratingOptions.map((opt) => (
                        <button key={opt.key} onClick={() => setFilters(prev => ({ ...prev, rating: opt.key }))} className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filters.rating === opt.key ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25' : isDarkMode ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20' : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'}`}>
                          {opt.key === 'all' ? opt.label : (<span className="flex items-center gap-1"><i className="ri-star-fill text-[10px]" />{opt.label}</span>)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Apply Button */}
                <div className={`p-5 border-t ${isDarkMode ? 'border-dark-200/50' : 'border-gray-200/50'}`}>
                  <button onClick={() => { setShowFilterModal(false); if (query.trim()) performSearch(query, filters); }} className="w-full py-3.5 rounded-2xl font-semibold text-white bg-gradient-to-r from-gold-dark to-gold-light hover:shadow-xl hover:shadow-gold/30 transition-all cursor-pointer whitespace-nowrap">
                    {t('search.applyFilters') || 'Apply Filters'}{activeFiltersCount > 0 && ` (${activeFiltersCount})`}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  // ==================== MODAL MODE RENDER ====================
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[200] flex flex-col"
          onClick={onClose}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ backdropFilter: 'blur(0px)' }}
            animate={{ backdropFilter: 'blur(12px) saturate(1.1)' }}
            exit={{ backdropFilter: 'blur(0px)' }}
            transition={{ duration: 0.35 }}
            className={`absolute inset-0 ${isDarkMode ? 'bg-black/55' : 'bg-white/55'}`}
          />

          {/* Search Panel */}
          <motion.div
            initial={{ y: -60, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: -60, opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="relative z-10 w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Bar: Back | Search Input | Filter */}
            <div className={`px-4 pt-4 pb-3 ${isDarkMode ? 'bg-dark-100/50' : 'bg-white/50'} backdrop-blur-xl border-b ${isDarkMode ? 'border-dark-200/30' : 'border-stone-200/30'}`}>
              <div className="flex items-center gap-3">
                {/* Back Button */}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-dark-200/60 hover:bg-dark-200 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} transition-colors cursor-pointer`}
                >
                  <i className="ri-arrow-left-line text-lg" />
                </motion.button>

                {/* Search Input */}
                <form onSubmit={handleSubmit} className="flex-1 relative">
                  <motion.div layoutId="searchMorphBar" className={`relative rounded-2xl overflow-hidden ${isDarkMode ? 'bg-dark-200/25 border-dark-200/25 backdrop-blur-md' : 'bg-white/30 border-stone-200/25 backdrop-blur-md'} border`}>
                    <i className={`ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    <input
                      ref={inputRef}
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={t('header.searchPlaceholder') || 'Search everything...'}
                      className={`w-full px-5 py-3.5 pl-12 pr-10 bg-transparent outline-none text-sm ${isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
                    />
                    {query && (
                      <motion.button
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-dark-200 text-gray-400' : 'bg-gray-200 text-gray-500'} cursor-pointer`}
                      >
                        <i className="ri-close-line text-sm" />
                      </motion.button>
                    )}
                  </motion.div>
                </form>

                {/* Filter Button */}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowFilterModal(true)}
                  className={`relative w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer ${
                    activeFiltersCount > 0
                      ? isDarkMode ? 'bg-gold/20 text-gold border border-gold/20' : 'bg-gold/15 text-gold border border-gold/20'
                      : isDarkMode ? 'bg-dark-200/60 hover:bg-dark-200 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  <i className="ri-filter-3-line text-lg" />
                  {activeFiltersCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </motion.button>
              </div>

              {/* Active Filter Chips */}
              {activeFiltersCount > 0 && (
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('search.filteringBy') || 'Filtering by'}:
                  </span>
                  {filters.category !== 'all' && (
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-gold/20 text-gold border border-gold/20' : 'bg-gold/15 text-gold-dark border border-gold/20'}`}>
                      <i className="ri-apps-line text-[10px]" />
                      {selectedCategoryName}
                      <button
                        onClick={() => {
                          setFilters(prev => ({ ...prev, category: 'all' }));
                          if (onClearCategory) onClearCategory();
                        }}
                        className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-gold/20 ml-1 cursor-pointer"
                      >
                        <i className="ri-close-line text-[10px]" />
                      </button>
                    </div>
                  )}
                  {filters.brand !== 'all' && (
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-gold/20 text-gold border border-gold/20' : 'bg-gold/15 text-gold-dark border border-gold/20'}`}>
                      <i className="ri-store-2-line text-[10px]" />
                      {selectedBrandName}
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, brand: 'all' }))}
                        className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-gold/20 ml-1 cursor-pointer"
                      >
                        <i className="ri-close-line text-[10px]" />
                      </button>
                    </div>
                  )}
                  {filters.color !== 'all' && (
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-gold/20 text-gold border border-gold/20' : 'bg-gold/15 text-gold-dark border border-gold/20'}`}>
                      <i className="ri-palette-line text-[10px]" />
                      {filters.color}
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, color: 'all' }))}
                        className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-gold/20 ml-1 cursor-pointer"
                      >
                        <i className="ri-close-line text-[10px]" />
                      </button>
                    </div>
                  )}
                  {filters.size !== 'all' && (
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-gold/20 text-gold border border-gold/20' : 'bg-gold/15 text-gold-dark border border-gold/20'}`}>
                      <i className="ri-ruler-line text-[10px]" />
                      {filters.size}
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, size: 'all' }))}
                        className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-gold/20 ml-1 cursor-pointer"
                      >
                        <i className="ri-close-line text-[10px]" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto px-4 pb-8" style={{ maxHeight: 'calc(100vh - 140px)' }}>

              {/* Recent Searches — shown when no query */}
              {!query && recentSearches.length > 0 && (
                <div className="py-5">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {t('home.recentSearches') || 'Recent Searches'}
                    </h4>
                    <button
                      onClick={() => { setRecentSearches([]); localStorage.removeItem('recentSearches'); }}
                      className="text-xs text-gold hover:text-gold-dark cursor-pointer"
                    >
                      {t('common.clear') || 'Clear All'}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {recentSearches.map((rs, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.04 }}
                        className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all cursor-pointer group ${
                          isDarkMode
                            ? 'bg-dark-200/15 hover:bg-dark-200/35 border border-dark-200/10'
                            : 'bg-white/25 hover:bg-white/45 border border-stone-200/10'
                        }`}
                      >
                        <button
                          onClick={() => setQuery(rs)}
                          className="flex items-center gap-3 flex-1 min-w-0 text-left"
                        >
                          <i className="ri-time-line text-gold text-sm flex-shrink-0" />
                          <span className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{rs}</span>
                        </button>
                        <button
                          onClick={() => deleteRecent(rs)}
                          className={`w-7 h-7 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer ${
                            isDarkMode ? 'hover:bg-red-500/10 text-gray-500 hover:text-red-400' : 'hover:bg-red-50 text-gray-400 hover:text-red-500'
                          }`}
                        >
                          <i className="ri-close-line text-sm" />
                        </button>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty recent state */}
              {!query && recentSearches.length === 0 && !loading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="py-12 flex flex-col items-center gap-3"
                >
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-dark-200/30 border border-dark-200/20' : 'bg-white/50 border border-stone-200/20'} backdrop-blur-md`}>
                    <i className={`ri-search-line text-2xl ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  </div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('search.startSearching') || 'Start searching'}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('search.typeToFind') || 'Type to find products, categories, and more'}
                  </p>
                </motion.div>
              )}

              {/* Loading */}
              {loading && (
                <div className="py-8 flex flex-col items-center gap-3">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                    className="w-8 h-8 rounded-full border-2 border-gold/20 border-t-gold"
                  />
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('common.loading') || 'Searching...'}
                  </span>
                </div>
              )}

              {/* No results */}
              {!loading && query && results.length === 0 && storeResults.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="py-12 flex flex-col items-center gap-3"
                >
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-dark-200/30 border border-dark-200/20' : 'bg-white/50 border border-stone-200/20'} backdrop-blur-md`}>
                    <i className={`ri-search-line text-2xl ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  </div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('errors.noResults') || 'No results found'}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Try searching with different keywords
                  </p>
                  {activeFiltersCount > 0 && (
                    <button
                      onClick={() => setFilters({ sortBy: 'relevance', priceRange: 'all', category: 'all', rating: 'all', brand: 'all', color: 'all', size: 'all' })}
                      className="text-xs text-gold hover:text-gold-dark cursor-pointer mt-2"
                    >
                      {t('search.clearAllFilters') || 'Clear all filters'}
                    </button>
                  )}
                </motion.div>
              )}

              {/* ======================== SEARCH RESULTS ======================== */}
              {!loading && query && (results.length > 0 || storeResults.length > 0) && (
                <div className="py-3 space-y-4">

                  {/* --- Delivery Mode Banner --- */}
                  {searchMode && (
                    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl mb-1 ${isDarkMode ? 'bg-gold/15 border border-gold/25' : 'bg-gold/10 border border-gold/20'}`}>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-gold/20' : 'bg-gold/15'}`}>
                        <i className={`text-base ${searchMode === 'delivery' ? 'ri-riding-fill' : searchMode === 'pickup' ? 'ri-walk-fill' : 'ri-calendar-fill'} ${isDarkMode ? 'text-gold' : 'text-gold-dark'}`} />
                      </div>
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-gold/90' : 'text-gold-dark/90'}`}>
                        {searchMode === 'delivery' ? (t('search.showingDeliveryResults') || 'Now showing delivery results') : searchMode === 'pickup' ? (t('search.showingPickupResults') || 'Now showing pickup results') : (t('search.showingScheduledResults') || 'Now showing scheduled results')}
                      </span>
                    </div>
                  )}

                  {/* --- Result Category Tabs with Animated Indicator --- */}
                  <LayoutGroup>
                    <div className="flex gap-1.5 overflow-x-auto hide-scrollbar">
                      {[
                        { key: 'all' as const, icon: 'ri-global-line', label: t('search.allResults') || 'همه', count: null },
                        { key: 'products' as const, icon: 'ri-shopping-bag-3-line', label: t('search.items') || 'آیتم‌ها', count: groupedResults['product']?.length },
                        { key: 'stores' as const, icon: 'ri-map-pin-line', label: t('search.locations') || 'مکان‌ها', count: storeResults.length },
                        { key: 'categories' as const, icon: 'ri-folder-line', label: t('search.categories') || 'دسته‌بندی‌ها', count: groupedResults['category']?.length },
                      ].map((tab) => (
                        <motion.button
                          key={tab.key}
                          onClick={() => setResultTab(tab.key)}
                          layout
                          whileTap={{ scale: reducedMotion ? 1 : 0.94 }}
                          className={`relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-medium cursor-pointer whitespace-nowrap ${
                            resultTab === tab.key
                              ? isDarkMode ? 'text-gold' : 'text-gold-dark'
                              : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {resultTab === tab.key && (
                            <motion.div
                              layoutId="activeResultTab"
                              className={`absolute inset-0 rounded-full ${
                                isDarkMode ? 'bg-gold/20 border border-gold/25' : 'bg-gold/15 border border-gold/20'
                              }`}
                              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                            />
                          )}
                          <i className={`${tab.icon} text-[10px] relative z-[1]`} />
                          <span className="relative z-[1]">{tab.label}</span>
                          {tab.count != null && tab.count > 0 && (
                            <span className="text-[10px] opacity-60 relative z-[1]">({tab.count})</span>
                          )}
                        </motion.button>
                      ))}
                    </div>
                  </LayoutGroup>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={resultTab}
                      initial={reducedMotion ? { opacity: 1 } : { opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={reducedMotion ? { opacity: 1 } : { opacity: 0, y: -6 }}
                      transition={{ duration: reducedMotion ? 0 : 0.2, ease: 'easeOut' }}
                    >
                      {/* --- PLACE (Stores) --- */}
                      {(resultTab === 'all' || resultTab === 'stores') && storeResults.length > 0 && (
                        <div className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-dark-200/30 border border-dark-200/20' : 'bg-white/40 border border-stone-200/20'} mb-4`}>
                          {resultTab === 'all' && (
                            <div className={`flex items-center px-5 py-3 border-b border-l-[4px] border-l-gold ${isDarkMode ? 'border-b-dark-200/20 bg-white/[0.03]' : 'border-b-stone-200/20 bg-black/[0.02]'}`}>
                              <span className={`text-[13px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                Place
                              </span>
                            </div>
                          )}
                          <div>
                            {storeResults.map((result, idx) => (
                              <button
                                key={result.id}
                                onClick={() => handleResultClick(result)}
                                className={`w-full px-4 py-3 text-left transition-colors cursor-pointer ${
                                  isDarkMode ? 'text-gray-200 hover:bg-dark-200/20' : 'text-gray-800 hover:bg-white/50'
                                } ${idx !== storeResults.length - 1 || (resultTab === 'all' && ((groupedResults['product'] && groupedResults['product'].length > 0) || (groupedResults['category'] && groupedResults['category'].length > 0))) ? (isDarkMode ? 'border-b border-dark-200/15' : 'border-b border-stone-200/15') : ''}`}
                              >
                                <p className="text-sm font-medium">{result.title}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* --- ITEM (Products) --- */}
                      {(resultTab === 'all' || resultTab === 'products') && groupedResults['product'] && groupedResults['product'].length > 0 && (
                        <div className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-dark-200/30 border border-dark-200/20' : 'bg-white/40 border border-stone-200/20'} mb-4`}>
                          {resultTab === 'all' && (
                            <div className={`flex items-center px-5 py-3 border-b border-l-[4px] border-l-gold ${isDarkMode ? 'border-b-dark-200/20 bg-white/[0.03]' : 'border-b-stone-200/20 bg-black/[0.02]'}`}>
                              <span className={`text-[13px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                Item
                              </span>
                            </div>
                          )}
                          <div>
                            {groupedResults['product'].slice(0, 6).map((result, idx) => (
                              <button
                                key={result.id}
                                onClick={() => handleResultClick(result)}
                                className={`w-full px-4 py-3 text-left transition-colors cursor-pointer ${
                                  isDarkMode ? 'text-gray-200 hover:bg-dark-200/20' : 'text-gray-800 hover:bg-white/50'
                                } ${idx !== groupedResults['product'].slice(0, 6).length - 1 || (resultTab === 'all' && (groupedResults['category'] && groupedResults['category'].length > 0)) ? (isDarkMode ? 'border-b border-dark-200/15' : 'border-b border-stone-200/15') : ''}`}
                              >
                                <p className="text-sm font-medium">{result.title}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* --- CATEGORY --- */}
                      {(resultTab === 'all' || resultTab === 'categories') && groupedResults['category'] && groupedResults['category'].length > 0 && (
                        <div className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-dark-200/30 border border-dark-200/20' : 'bg-white/40 border border-stone-200/20'} mb-4`}>
                          {resultTab === 'all' && (
                            <div className={`flex items-center px-5 py-3 border-b border-l-[4px] border-l-gold ${isDarkMode ? 'border-b-dark-200/20 bg-white/[0.03]' : 'border-b-stone-200/20 bg-black/[0.02]'}`}>
                              <span className={`text-[13px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                Category
                              </span>
                            </div>
                          )}
                          <div>
                            {groupedResults['category'].map((result, idx) => (
                              <button
                                key={result.id}
                                onClick={() => handleResultClick(result)}
                                className={`w-full px-4 py-3 text-left transition-colors cursor-pointer ${
                                  isDarkMode ? 'text-gray-200 hover:bg-dark-200/20' : 'text-gray-800 hover:bg-white/50'
                                } ${idx !== groupedResults['category'].length - 1 ? (isDarkMode ? 'border-b border-dark-200/15' : 'border-b border-stone-200/15') : ''}`}
                              >
                                <p className="text-sm font-medium">{result.title}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  {/* --- View All Results Button --- */}
                  {query.trim() && (
                    <button
                      onClick={() => {
                        saveRecent(query);
                        navigate(`/search?q=${encodeURIComponent(query)}`);
                        onClose();
                      }}
                      className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-semibold transition-all cursor-pointer ${
                        isDarkMode
                          ? 'bg-gold/15 text-gold border border-gold/25 hover:bg-gold/25'
                          : 'bg-gold/10 text-gold-dark border border-gold/20 hover:bg-gold/20'
                      }`}
                    >
                      <i className="ri-arrow-right-line" />
                      <span>
                        {t('search.viewAllResults') || 'View all results for'} &lsquo;{query}&rsquo;
                      </span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          {/* Filter Modal */}
          <AnimatePresence>
            {showFilterModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[210] flex items-end md:items-center justify-center"
                onClick={() => setShowFilterModal(false)}
              >
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-black/60' : 'bg-white/60'} backdrop-blur-sm`} />
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                  className={`relative w-full md:max-w-md md:rounded-2xl rounded-t-3xl ${isDarkMode ? 'bg-dark-100/80 border-dark-200/30 backdrop-blur-xl' : 'bg-white/80 border-stone-200/30 backdrop-blur-xl'} border shadow-2xl max-h-[80vh] overflow-hidden flex flex-col`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Handle + Header */}
                  <div className="flex justify-center pt-3 pb-1">
                    <div className={`w-10 h-1 rounded-full ${isDarkMode ? 'bg-dark-300' : 'bg-gray-300'}`} />
                  </div>
                  <div className={`flex items-center justify-between px-5 pb-3 border-b ${isDarkMode ? 'border-dark-200/50' : 'border-gray-200/50'}`}>
                    <h3 className="text-lg font-bold">{t('search.filters') || 'Filters'}</h3>
                    <div className="flex items-center gap-2">
                      {activeFiltersCount > 0 && (
                        <button
                          onClick={() => setFilters({ sortBy: 'relevance', priceRange: 'all', category: 'all', rating: 'all', brand: 'all', color: 'all', size: 'all' })}
                          className="text-xs text-gold hover:text-gold-dark cursor-pointer"
                        >
                          {t('common.reset') || 'Reset'}
                        </button>
                      )}
                      <button
                        onClick={() => setShowFilterModal(false)}
                        className={`w-8 h-8 rounded-xl flex items-center justify-center cursor-pointer ${isDarkMode ? 'bg-dark-200/30 hover:bg-dark-300/40 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                      >
                        <i className="ri-close-line" />
                      </button>
                    </div>
                  </div>

                  {/* Filter Options */}
                  <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
                    {/* Sort By */}
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('search.sortBy') || 'Sort By'}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {sortOptions.map((opt) => (
                          <button
                            key={opt.key}
                            onClick={() => setFilters(prev => ({ ...prev, sortBy: opt.key }))}
                            className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                              filters.sortBy === opt.key
                                ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25'
                                : isDarkMode
                                  ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20'
                                  : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Category Filter */}
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('nav.categories') || 'Categories'}
                      </h4>
                      {categoriesLoading ? (
                        <div className="flex flex-wrap gap-2">
                          {[1,2,3,4,5].map(i => (
                            <div key={i} className={`w-20 h-8 rounded-xl animate-pulse ${isDarkMode ? 'bg-dark-200/30' : 'bg-gray-200/50'}`} />
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => setFilters(prev => ({ ...prev, category: 'all' }))}
                            className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                              filters.category === 'all'
                                ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25'
                                : isDarkMode
                                  ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20'
                                  : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'
                            }`}
                          >
                            {t('categories.all') || 'All'}
                          </button>
                          {categories.map((cat) => (
                            <button
                              key={cat.id}
                              onClick={() => setFilters(prev => ({ ...prev, category: String(cat.id) }))}
                              className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                                filters.category === String(cat.id)
                                  ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25'
                                  : isDarkMode
                                    ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20'
                                    : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'
                              }`}
                            >
                              {cat.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Brand Filter */}
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('search.brand') || 'Brand'}
                      </h4>
                      {brandsLoading ? (
                        <div className="flex flex-wrap gap-2">
                          {[1,2,3,4,5].map(i => (
                            <div key={i} className={`w-20 h-8 rounded-xl animate-pulse ${isDarkMode ? 'bg-dark-200/30' : 'bg-gray-200/50'}`} />
                          ))}
                        </div>
                      ) : brands.length === 0 ? (
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {t('search.noBrands') || 'No brands available'}
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => setFilters(prev => ({ ...prev, brand: 'all' }))}
                            className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                              filters.brand === 'all'
                                ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25'
                                : isDarkMode
                                  ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20'
                                  : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'
                            }`}
                          >
                            {t('search.allBrands') || 'All Brands'}
                          </button>
                          {brands.map((brand) => (
                            <button
                              key={brand.id}
                              onClick={() => setFilters(prev => ({ ...prev, brand: String(brand.id) }))}
                              className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                                filters.brand === String(brand.id)
                                  ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25'
                                  : isDarkMode
                                    ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20'
                                    : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'
                              }`}
                            >
                              {brand.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Color Filter */}
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('search.color') || 'Color'}
                      </h4>
                      {colorsLoading ? (
                        <div className="flex flex-wrap gap-2">
                          {[1,2,3,4,5].map(i => (
                            <div key={i} className={`w-12 h-8 rounded-xl animate-pulse ${isDarkMode ? 'bg-dark-200/30' : 'bg-gray-200/50'}`} />
                          ))}
                        </div>
                      ) : colors.length === 0 ? (
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {t('search.noColors') || 'No colors available'}
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => setFilters(prev => ({ ...prev, color: 'all' }))}
                            className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                              filters.color === 'all'
                                ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25'
                                : isDarkMode
                                  ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20'
                                  : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'
                            }`}
                          >
                            {t('search.allColors') || 'All Colors'}
                          </button>
                          {colors.map((color) => (
                            <button
                              key={color}
                              onClick={() => setFilters(prev => ({ ...prev, color }))}
                              className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                                filters.color === color
                                  ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25'
                                  : isDarkMode
                                    ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20'
                                    : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'
                              }`}
                            >
                              {color}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Size Filter */}
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('search.size') || 'Size'}
                      </h4>
                      {sizesLoading ? (
                        <div className="flex flex-wrap gap-2">
                          {[1,2,3,4,5].map(i => (
                            <div key={i} className={`w-12 h-8 rounded-xl animate-pulse ${isDarkMode ? 'bg-dark-200/30' : 'bg-gray-200/50'}`} />
                          ))}
                        </div>
                      ) : sizes.length === 0 ? (
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {t('search.noSizes') || 'No sizes available'}
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => setFilters(prev => ({ ...prev, size: 'all' }))}
                            className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                              filters.size === 'all'
                                ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25'
                                : isDarkMode
                                  ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20'
                                  : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'
                            }`}
                          >
                            {t('search.allSizes') || 'All Sizes'}
                          </button>
                          {sizes.map((size) => (
                            <button
                              key={size}
                              onClick={() => setFilters(prev => ({ ...prev, size }))}
                              className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                                filters.size === size
                                  ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25'
                                  : isDarkMode
                                    ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20'
                                    : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'
                              }`}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Price Range */}
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('search.priceRange') || 'Price Range'}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {priceRanges.map((opt) => (
                          <button
                            key={opt.key}
                            onClick={() => setFilters(prev => ({ ...prev, priceRange: opt.key }))}
                            className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                              filters.priceRange === opt.key
                                ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25'
                                : isDarkMode
                                  ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20'
                                  : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Rating */}
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('search.rating') || 'Rating'}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {ratingOptions.map((opt) => (
                          <button
                            key={opt.key}
                            onClick={() => setFilters(prev => ({ ...prev, rating: opt.key }))}
                            className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                              filters.rating === opt.key
                                ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/25'
                                : isDarkMode
                                  ? 'bg-dark-200/25 text-gray-400 hover:bg-dark-200/45 border border-dark-200/20'
                                  : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'
                            }`}
                          >
                            {opt.key === 'all' ? opt.label : (
                              <span className="flex items-center gap-1">
                                <i className="ri-star-fill text-[10px]" />
                                {opt.label}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Apply Button */}
                  <div className={`p-5 border-t ${isDarkMode ? 'border-dark-200/50' : 'border-gray-200/50'}`}>
                    <button
                      onClick={() => {
                        setShowFilterModal(false);
                        if (query.trim()) performSearch(query, filters);
                      }}
                      className="w-full py-3.5 rounded-2xl font-semibold text-white bg-gradient-to-r from-gold-dark to-gold-light hover:shadow-xl hover:shadow-gold/30 transition-all cursor-pointer whitespace-nowrap"
                    >
                      {t('search.applyFilters') || 'Apply Filters'}
                      {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}